import React, { useState, useEffect } from 'react';
import { useStudentAuth } from '../../context/StudentAuthContext';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

const MyProfile = () => {
  const { studentApi } = useStudentAuth();
  const { user } = useStudentAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [academicRecords, setAcademicRecords] = useState([]);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      const [profileRes, gradesRes] = await Promise.all([
        studentApi.get('/student-portal/profile'),
        studentApi.get('/student-portal/grades')
      ]);

      setProfile(profileRes.data);
      setAcademicRecords(gradesRes.data || []);

    } catch (error) {
      toast.error('Failed to load profile data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const profileSchema = Yup.object().shape({
    surname: Yup.string().required('Surname is required'),
    othernames: Yup.string().required('Other names are required'),
    sex: Yup.string().oneOf(['Male', 'Female', 'Other']),
    dob: Yup.date().required('Date of birth is required'),
    address: Yup.string(),
    guardian: Yup.string(),
    guardian_address: Yup.string(),
    guardian_phone: Yup.string(),
  });

  const passwordSchema = Yup.object().shape({
    current_password: Yup.string().required('Current password is required'),
    new_password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('New password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
      .required('Please confirm your password'),
  });

  useEffect(() => {
    fetchProfileData();
  }, [user]);

  
  const handleUpdateProfile = async (values, { setSubmitting }) => {
    try {
      // API call to update profile
      setProfile({ ...profile, ...values });
      toast.success('Profile updated successfully');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (values, { setSubmitting, resetForm }) => {
    try {
      // API call to change password
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
          <p className="text-gray-600">View and manage your personal information</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="btn-primary flex items-center"
          >
            <i className="fas fa-edit mr-2"></i> Edit Profile
          </button>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="btn-secondary flex items-center"
          >
            <i className="fas fa-key mr-2"></i> Change Password
          </button>
        </div>
      </div>

      {/* Profile Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Information */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
              <p className="text-gray-600 text-sm">Your personal details and contact information</p>
            </div>
            <div className="text-sm text-gray-500">
              Student ID: {profile?.student_reg || '--'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Full Name</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.surname} {profile?.othernames}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.dob ? format(new Date(profile.dob), 'dd/MM/yyyy') : '--'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Gender</label>
                <div className="mt-1">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {profile?.sex || '--'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Age</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.dob ? new Date().getFullYear() - new Date(profile.dob).getFullYear() : '--'} years
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Address</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.address || 'Not provided'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Guardian Name</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.guardian || 'Not provided'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Guardian Phone</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.guardian_phone || 'Not provided'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Guardian Address</label>
                <div className="mt-1 text-lg font-medium text-gray-900">
                  {profile?.guardian_address || 'Not provided'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-primary-100 rounded-full mb-4">
              <i className="fas fa-user-graduate text-primary-600 text-3xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{profile?.surname} {profile?.othernames}</h3>
            <p className="text-gray-500">Student</p>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Student ID:</span>
              <span className="font-medium">{profile?.student_reg || '--'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Status:</span>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                {/* Active */}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Enrolled:</span>
              <span className="font-medium">
                {profile?.created_at ? format(new Date(profile.created_at), 'dd/MM/yyyy') : '--'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Class:</span>
              <span className="font-medium">{profile?.class_name}</span>
            </div>
            {/* <div className="flex justify-between items-center">
              <span className="text-gray-600">Attendance:</span>
              <span className="font-medium text-green-600">95%</span>
            </div> */}
          </div>
        </div>
      </div>

      {/* Academic Records */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Academic Records</h3>
              <p className="text-gray-600 text-sm">Your academic performance history</p>
            </div>
            <div className="text-sm text-gray-500">
              {academicRecords.length} records
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Academic Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Term
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Average Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {academicRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {record.academic_session}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{record.class}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{record.term}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {record.position}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm font-bold ${
                      record.average >= 90 ? 'text-green-600' :
                      record.average >= 80 ? 'text-green-500' :
                      record.average >= 70 ? 'text-blue-500' :
                      record.average >= 60 ? 'text-yellow-500' :
                      'text-red-500'
                    }`}>
                      {record.average.toFixed(1)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      record.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.comment}
                    </span>
                  </td>
                  {/* <td className="px-6 py-4 text-sm font-medium">
                    <button className="text-primary-600 hover:text-primary-900">
                      <i className="fas fa-eye mr-1"></i> View Details
                    </button>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

     
      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Profile"
        size="lg"
      >
        <Formik
          initialValues={{
            surname: profile?.surname || '',
            othernames: profile?.othernames || '',
            sex: profile?.sex || 'Male',
            dob: profile?.dob ? format(new Date(profile.dob), 'yyyy-MM-dd') : '',
            address: profile?.address || '',
            guardian: profile?.guardian || '',
            guardian_address: profile?.guardian_address || '',
            guardian_phone: profile?.guardian_phone || '',
          }}
          validationSchema={profileSchema}
          onSubmit={handleUpdateProfile}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surname *
                  </label>
                  <Field
                    type="text"
                    name="surname"
                    className="input-field"
                  />
                  {errors.surname && touched.surname && (
                    <div className="text-red-500 text-sm mt-1">{errors.surname}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Other Names *
                  </label>
                  <Field
                    type="text"
                    name="othernames"
                    className="input-field"
                  />
                  {errors.othernames && touched.othernames && (
                    <div className="text-red-500 text-sm mt-1">{errors.othernames}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <Field as="select" name="sex" className="input-field">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Field>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth *
                  </label>
                  <Field
                    type="date"
                    name="dob"
                    className="input-field"
                  />
                  {errors.dob && touched.dob && (
                    <div className="text-red-500 text-sm mt-1">{errors.dob}</div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <Field
                  type="text"
                  name="address"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Name
                </label>
                <Field
                  type="text"
                  name="guardian"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Phone
                </label>
                <Field
                  type="text"
                  name="guardian_phone"
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Address
                </label>
                <Field
                  type="text"
                  name="guardian_address"
                  className="input-field"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Change Password"
      >
        <Formik
          initialValues={{
            current_password: '',
            new_password: '',
            confirm_password: '',
          }}
          validationSchema={passwordSchema}
          onSubmit={handleChangePassword}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password *
                </label>
                <Field
                  type="password"
                  name="current_password"
                  className="input-field"
                />
                {errors.current_password && touched.current_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.current_password}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <Field
                  type="password"
                  name="new_password"
                  className="input-field"
                />
                {errors.new_password && touched.new_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.new_password}</div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 8 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password *
                </label>
                <Field
                  type="password"
                  name="confirm_password"
                  className="input-field"
                />
                {errors.confirm_password && touched.confirm_password && (
                  <div className="text-red-500 text-sm mt-1">{errors.confirm_password}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Changing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-key mr-2"></i>
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default MyProfile;